import { toast } from 'react-toastify';

import { PlacesApiClient } from '@/services/places/places_api';
import { GooglePlacesApiAdapter } from '@/services/places/places_api/adapters/google_places';
import { mapPlaceToAddressData } from '@/services/places/places_api/mappers/place_to_address_data';
import { viaCepSearch } from '@/services/places/places_api/utils/viacep';

let placesClient: PlacesApiClient | null = null;

export interface FormAddressData {
  street: string;
  city: string;
  state: string;
  neighborhood: string;
  zipcode: string;
}

enum AddressSearchErrorType {
  INVALID_CEP = 'INVALID_CEP',
  VIACEP_FAILED = 'VIACEP_FAILED',
  PLACES_API_FAILED = 'PLACES_API_FAILED',
  PLACES_API_UNAVAILABLE = 'PLACES_API_UNAVAILABLE',
  NO_RESULTS_FOUND = 'NO_RESULTS_FOUND',
  NETWORK_ERROR = 'NETWORK_ERROR',
  UNKNOWN_ERROR = 'UNKNOWN_ERROR',
}

interface AddressSearchError {
  type: AddressSearchErrorType;
  message: string;
  originalError?: unknown;
  source: 'viacep' | 'places-api' | 'general';
}

const handleAddressSearchError = (
  error: unknown,
  source: 'viacep' | 'places-api' | 'general',
): AddressSearchError => {
  if (error instanceof Error) {
    if (error.message.includes('fetch') || error.message.includes('network')) {
      return {
        type: AddressSearchErrorType.NETWORK_ERROR,
        message: 'Erro de conexão. Verifique sua internet.',
        originalError: error,
        source,
      };
    }

    if (error.message.includes('CEP not found') || error.message.includes('Invalid CEP')) {
      return {
        type: AddressSearchErrorType.INVALID_CEP,
        message: 'CEP não encontrado. Verifique o número digitado.',
        originalError: error,
        source,
      };
    }

    if (source === 'viacep') {
      return {
        type: AddressSearchErrorType.VIACEP_FAILED,
        message: 'ViaCEP temporariamente indisponível.',
        originalError: error,
        source,
      };
    }

    if (source === 'places-api') {
      return {
        type: AddressSearchErrorType.PLACES_API_FAILED,
        message: 'Serviço de endereços temporariamente indisponível.',
        originalError: error,
        source,
      };
    }
  }

  return {
    type: AddressSearchErrorType.UNKNOWN_ERROR,
    message: 'Erro inesperado ao buscar endereço.',
    originalError: error,
    source,
  };
};

const initializePlacesClient = () => {
  if (!placesClient) {
    try {
      const googleAdapter = new GooglePlacesApiAdapter({
        language: 'pt-BR',
        countries: ['BR'],
        region: 'BR',
      });

      placesClient = new PlacesApiClient(googleAdapter);
    } catch (error) {
      console.error('Failed to initialize Places API client:', error);
      toast.error(
        'Erro ao inicializar serviço de endereços. Algumas funcionalidades podem estar limitadas.',
      );
      return null;
    }
  }
  return placesClient;
};

const convertViaCepToFormFormat = (viaCepData: any): FormAddressData => {
  return {
    street: viaCepData.logradouro || '',
    city: viaCepData.localidade || '',
    state: viaCepData.uf || '',
    neighborhood: viaCepData.bairro || '',
    zipcode: viaCepData.cep || '',
  };
};

const isValidCEP = (input: string): boolean => {
  const cleaned = input.replace(/\D/g, '');
  return cleaned.length === 8;
};

const convertToFormFormat = (addressData: any): FormAddressData | null => {
  if (!addressData) return null;

  return {
    street: addressData.street || '',
    city: addressData.city || '',
    state: addressData.state || '',
    neighborhood: addressData.neighborhood || '',
    zipcode: addressData.zipcode || '',
  };
};

export const useEnhancedAddressSearchWithCompletion = async (
  input: string,
): Promise<FormAddressData | string | null> => {
  if (isValidCEP(input)) {
    try {
      const viaCepData = await viaCepSearch(input);

      if (viaCepData) {
        const viaCepResult = convertViaCepToFormFormat(viaCepData);

        const isComplete =
          viaCepResult.street &&
          viaCepResult.neighborhood &&
          viaCepResult.city &&
          viaCepResult.state;

        if (isComplete) {
          return viaCepResult;
        }

        try {
          const client = initializePlacesClient();
          if (!client) {
            toast.warning('Serviço de busca avançada indisponível. Usando dados parciais.');
            return viaCepResult;
          }

          const searchQuery = [
            viaCepResult.street || '',
            viaCepResult.neighborhood || '',
            viaCepResult.city || '',
            viaCepResult.state || '',
            viaCepResult.zipcode,
          ]
            .filter(Boolean)
            .join(' ')
            .trim();

          const suggestions = await client.suggestPlaces({ input: searchQuery });

          if (suggestions.length > 0) {
            const placeDetails = await client.getPlaceDetails(suggestions[0]!.placeId, {
              fields: ['location'],
            });

            const placesData = mapPlaceToAddressData(placeDetails);
            const placesResult = convertToFormFormat(placesData);

            const enhancedResult = {
              street: viaCepResult.street || placesResult?.street || '',
              city: viaCepResult.city || placesResult?.city || '',
              state: viaCepResult.state || placesResult?.state || '',
              neighborhood: viaCepResult.neighborhood || placesResult?.neighborhood || '',
              zipcode: viaCepResult.zipcode || placesResult?.zipcode || '',
            };

            return enhancedResult;
          }
        } catch (placesError) {
          console.error('Places API enhancement failed:', placesError);
          return 'CEP informado é inválido.';
        }

        return viaCepResult;
      }
    } catch (error) {
      console.error('ViaCEP search failed in useEnhancedAddressSearchWithCompletion:', error);
    }
  }

  try {
    const client = initializePlacesClient();
    if (!client) {
      const errorInfo: AddressSearchError = {
        type: AddressSearchErrorType.PLACES_API_UNAVAILABLE,
        message: 'Serviço de busca de endereços não disponível.',
        source: 'places-api',
      };
      toast.error(errorInfo.message);
      return null;
    }

    const suggestions = await client.suggestPlaces({ input });

    if (suggestions.length === 0) {
      toast.error('Nenhum endereço encontrado para esta busca.');
      return null;
    }

    const placeDetails = await client.getPlaceDetails(suggestions[0]!.placeId, {
      fields: ['location'],
    });

    const addressData = mapPlaceToAddressData(placeDetails);
    const result = convertToFormFormat(addressData);
    return result;
  } catch (error) {
    console.error('Places API search failed:', error);
    const errorInfo = handleAddressSearchError(error, 'places-api');

    if (errorInfo.type === AddressSearchErrorType.NETWORK_ERROR) {
      toast.error('Erro de conexão. Verifique sua internet e tente novamente.');
    } else if (errorInfo.type === AddressSearchErrorType.INVALID_CEP) {
      toast.error('CEP inválido. Verifique o número digitado.');
    } else {
      toast.error('Erro ao buscar endereço. Tente novamente em alguns instantes.');
    }

    return null;
  }
};

export const useEnhancedAddressSearch = async (input: string): Promise<FormAddressData | null> => {
  if (isValidCEP(input)) {
    try {
      const viaCepData = await viaCepSearch(input);

      if (viaCepData) {
        return convertViaCepToFormFormat(viaCepData);
      }
    } catch (error) {
      console.error('ViaCEP search failed in useEnhancedAddressSearch:', error);
    }
  }

  try {
    const client = initializePlacesClient();
    if (!client) {
      const errorInfo: AddressSearchError = {
        type: AddressSearchErrorType.PLACES_API_UNAVAILABLE,
        message: 'Serviço de busca de endereços não disponível.',
        source: 'places-api',
      };
      toast.error(errorInfo.message);
      return null;
    }

    const suggestions = await client.suggestPlaces({ input });

    if (suggestions.length === 0) {
      toast.error('Nenhum endereço encontrado para esta busca.');
      return null;
    }

    const placeDetails = await client.getPlaceDetails(suggestions[0]!.placeId, {
      fields: ['location'],
    });

    const addressData = mapPlaceToAddressData(placeDetails);
    const result = convertToFormFormat(addressData);
    return result;
  } catch (error) {
    console.error('Places API search failed in searchPlacesByText:', error);
    const errorInfo = handleAddressSearchError(error, 'places-api');

    if (errorInfo.type === AddressSearchErrorType.NETWORK_ERROR) {
      toast.error('Erro de conexão. Verifique sua internet e tente novamente.');
    } else if (errorInfo.type === AddressSearchErrorType.INVALID_CEP) {
      toast.error('CEP inválido. Verifique o número digitado.');
    } else {
      toast.error('Erro ao buscar endereço. Tente novamente em alguns instantes.');
    }

    return null;
  }
};

export const useViaCepOnlySearch = async (zipcode: string): Promise<FormAddressData | null> => {
  if (isValidCEP(zipcode)) {
    try {
      const viaCepData = await viaCepSearch(zipcode);

      if (viaCepData) {
        return convertViaCepToFormFormat(viaCepData);
      }
    } catch (error) {
      console.error('ViaCEP search failed in useViaCepOnlySearch:', error);
    }
  }

  try {
    const client = initializePlacesClient();
    if (!client) {
      throw new Error('Places API client not available');
    }

    const places = await client.searchPlaces(zipcode);

    if (places.length === 0) {
      return null;
    }

    const addressData = mapPlaceToAddressData(places[0]!);
    return convertToFormFormat(addressData);
  } catch (error) {
    console.error('Places API search failed in useViaCepOnlySearch:', error);
    toast.error('Não foi possível encontrar o endereço. Verifique o CEP e tente novamente.');
    return null;
  }
};
